// The add-a-card flow: a full screen with its own header, a close button and progress, one step
// per screen and nothing else on it. Opened from "Add a card" on Settings, Cards and banks (and by
// itself the first time, for the first card). Every card starts fresh at the gate. Status rows are
// checks Hush makes by itself (a spinner, then a green check, "Hush checks this"); nothing here
// looks like a box to tick. Rules and copy: shared/card-flow.ts.
import type { FlowCompareDto, FlowExampleDto, OverviewDto } from '../../../shared/api';
import { alertSteps, bankBySender, type BankEntry } from '../../../shared/banks';
import {
  ALERTS_ON_TITLE,
  ALERTS_TO_GMAIL,
  CARD_ALERTS_BODY,
  CARD_ALERTS_TITLE,
  CARD_SPECIFIC,
  COMPARE_TITLE,
  confirmLink,
  COVERED,
  DONE_BODY,
  DONE_TITLE,
  EVERY_CARD,
  EXAMPLE_BODY,
  EXAMPLE_TITLE,
  EXAMPLE_WAIT,
  FILTER_COMPUTER,
  FILTER_FIELDS_ONLY,
  FILTER_FORWARD,
  FILTER_LEAVE_FORWARDING,
  FILTER_OPEN,
  FILTER_TITLE,
  FILTER_WHY_SUBJECT,
  FILTER_WHY_WORDS,
  FILTER_WORDS_HOW,
  FILTER_WORDS_LABEL,
  FLOW_TITLE,
  FROM_ONLY_WARNING,
  GATE_Q,
  GMAIL_OTHER,
  GMAIL_Q,
  HUSH_CHECKS,
  NICKNAME_LABEL,
  NOT_SURE_BODY,
  NOT_SURE_TITLE,
  NOT_WORKING_HELP,
  SUBJECT_WHY,
  VERIFY_ARRIVED,
  VERIFY_DONE,
  VERIFY_LINK,
  VERIFY_NO_LINK,
  VERIFY_TITLE,
  VERIFY_WAIT,
  type FlowStep,
} from '../../../shared/card-flow';
import { quotedPhrase } from '../../../shared/filter-choice';
import { gmailForwardSteps } from '../../../shared/setup-copy';
import { esc } from '../format';

/** The flow's state (main.ts keeps it in ui.cardFlow; memory only). */
export interface FlowState {
  step: FlowStep;
  /** "Turn on transaction alerts" was shown in this flow: the card-alerts step after Compare is skipped. */
  alertsShown: boolean;
  /** When this flow started: only an example forwarded after it counts (never an earlier card's). */
  startedAt: string;
  /** The Gmail address chosen; null: not chosen (the first card, or "a different Gmail"). */
  gmail: string | null;
  /** "A different Gmail address" was chosen. */
  gmailOther: boolean;
  /** Verification was shown (and finished) in this flow. */
  verifiedHere: boolean;
  example: FlowExampleDto['example'];
  notAlert: boolean;
  compare: FlowCompareDto | null;
  nickname: string;
  /** The card added (after the compare step). */
  cardId: string | null;
  /** Steps shown so far, for Back and the progress dots. */
  trail: FlowStep[];
  /** The Gmail addresses Hush knew when the flow started (to spot the one a new verification taught). */
  knownAtStart: string[];
}

export const newFlow = (now: Date): FlowState => ({
  step: 'gate',
  alertsShown: false,
  startedAt: now.toISOString(),
  gmail: null,
  gmailOther: false,
  verifiedHere: false,
  example: null,
  notAlert: false,
  compare: null,
  nickname: '',
  cardId: null,
  trail: [],
  knownAtStart: [],
});

const CHECK_SVG =
  '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 6.5l2.5 2.5 4.5-5"/></svg>';

/** An automatic check: a spinner while Hush waits, then a green check. Never a box to tick. */
export function checkRow(label: string, done: boolean): string {
  return `<div class="hcheck ${done ? 'ok' : 'wait'}" role="status"><span class="hmark" aria-hidden="true">${done ? CHECK_SVG : '<span class="hspin"></span>'}</span><span class="hlabel">${esc(label)}<small>${esc(HUSH_CHECKS)}</small></span></div>`;
}

const copyRow = (id: string, value: string, what: string) =>
  `<div class="addr"><code id="${id}">${esc(value)}</code><button class="btn" data-a="copy" data-from="${id}" data-what="${esc(what)}">Copy</button></div>`;

/** The "Not working?" link for a step (placeholder until its help is written). */
export function notWorkingLink(step: FlowStep): string {
  const help = NOT_WORKING_HELP[step];
  return help ? `<button class="linkbtn" data-a="flow-help" data-step="${step}">Not working?</button>` : '';
}

/**
 * Progress: the screens shown so far plus the ones this card will most likely still take (the
 * Gmail choice when Hush knows an address, verification when that address is not verified, the
 * filter unless covered). An estimate that settles as answers come in.
 */
export function progressOf(f: FlowState, d: OverviewDto): { at: number; total: number } {
  const known = d.cardsBanks.gmail.length > 0;
  const covered = f.compare?.result.kind === 'covered';
  const verifyAfter = !!f.compare && !f.compare.gmailVerified && !f.verifiedHere;
  // Covered: the card's own alerts step, then done (unless alerts were already shown).
  const alertsAfter = f.alertsShown ? 1 : 2;
  const left: Record<FlowStep, number> = {
    gate: (known ? 1 : 0) + 4,
    'alerts-on': (known ? 1 : 0) + 4,
    'not-sure': (known ? 1 : 0) + 4,
    gmail: f.gmailOther ? 5 : 4,
    verify: f.example ? (covered ? alertsAfter : 2) : 4,
    example: 3,
    compare: covered ? (verifyAfter ? 1 : 0) + alertsAfter : verifyAfter ? 3 : 2,
    filter: 1,
    'card-alerts': 1,
    done: 0,
  };
  return { at: f.trail.length, total: f.trail.length + 1 + left[f.step] };
}

function progress(f: FlowState, d: OverviewDto): string {
  const { at, total } = progressOf(f, d);
  return `<div class="fprog" role="img" aria-label="Step ${at + 1} of ${total}">${Array.from({ length: total }, (_, i) => `<span class="${i < at ? 'done' : i === at ? 'now' : ''}"></span>`).join('')}</div>`;
}

function header(f: FlowState, d: OverviewDto): string {
  const back = f.trail.length && f.step !== 'done' ? `<button class="btn" data-a="flow-back">Back</button>` : '<span></span>';
  return `<header class="fhead">${back}<h1>${esc(FLOW_TITLE)}</h1><button class="btn" data-a="flow-close" aria-label="Close">Close</button></header>${progress(f, d)}`;
}

const screen = (title: string, body: string, step: FlowStep) => `<section class="fbody"><h2>${esc(title)}</h2>${body}${notWorkingLink(step)}</section>`;

function gate(): string {
  return screen(
    GATE_Q,
    `<div class="fchoices"><button class="btn primary" data-a="flow-gate" data-answer="yes">Yes</button><button class="btn" data-a="flow-gate" data-answer="no">No</button><button class="btn" data-a="flow-gate" data-answer="not-sure">Not sure</button></div>
  <p class="hint">${esc(EVERY_CARD)}</p>`,
    'gate',
  );
}

/**
 * How to turn alerts on, shared by both alert screens: the bank's own steps once their menu names
 * are verified (`stepsVerified` in shared/banks.ts), else the general steps. No bank picker: until a
 * bank's steps are verified every bank reads the same.
 */
function alertsBody(bank: BankEntry | undefined, next: string): string {
  const how = alertSteps(bank);
  return `<ol>${how.steps.map((s) => `<li>${esc(s)}</li>`).join('')}</ol>
  <p class="hint">${esc(ALERTS_TO_GMAIL)}</p>
  <div class="btns"><button class="btn primary" ${next}>Alerts are on</button></div>`;
}

/** "Turn on transaction alerts" (the gate said No): the bank is not known yet, so the general steps. */
function alertsOn(): string {
  return screen(ALERTS_ON_TITLE, alertsBody(undefined, 'data-a="flow-alerts-on"'), 'alerts-on');
}

function notSure(): string {
  return screen(
    NOT_SURE_TITLE,
    `<p>${esc(NOT_SURE_BODY)}</p><div class="fchoices"><button class="btn primary" data-a="flow-gate" data-answer="yes">Found one</button><button class="btn" data-a="flow-gate" data-answer="no">None</button></div>`,
    'not-sure',
  );
}

function gmail(f: FlowState, d: OverviewDto): string {
  const opts = d.cardsBanks.gmail
    .map((g) => `<button class="opt" data-a="flow-gmail" data-gmail="${esc(g.address)}" aria-pressed="${f.gmail === g.address}"><b>${esc(g.address)}</b></button>`)
    .join('');
  return screen(GMAIL_Q, `<div class="fopts">${opts}<button class="opt" data-a="flow-gmail" data-gmail="" aria-pressed="${f.gmailOther}"><b>${esc(GMAIL_OTHER)}</b></button></div>`, 'gmail');
}

function example(f: FlowState, d: OverviewDto): string {
  const got = !!f.example;
  return screen(
    EXAMPLE_TITLE,
    `<p>${esc(EXAMPLE_BODY)}</p>${copyRow('flow-addr', d.user.forwardAddress, 'Address')}
  ${checkRow(got ? `Alert from ${f.example!.bankName} received` : EXAMPLE_WAIT, got)}
  ${f.notAlert && !got ? '<p class="never">That email did not look like a card alert. Forward one about a purchase.</p>' : ''}
  ${got ? '<div class="btns"><button class="btn primary" data-a="flow-next" data-to="compare">Continue</button></div>' : ''}`,
    'example',
  );
}

/**
 * "Checking your alert": only Hush's checks and the card's name. What Gmail needs comes on the
 * next screen (the filter, or the card's own alerts), never here.
 */
function compare(f: FlowState): string {
  const e = f.example;
  const c = f.compare;
  const checks = [
    checkRow(e ? `Read the alert from ${e.bankName}` : 'Reading the alert', !!e),
    checkRow(e?.last4 ? `Card ending ${e.last4}` : 'This alert does not show the card number', !!e),
  ].join('');
  if (!c) return screen(COMPARE_TITLE, checks, 'compare');
  const name = `<label class="fld"><span>${esc(NICKNAME_LABEL)}</span><input id="flow-nick" type="text" maxlength="40" value="${esc(f.nickname)}" placeholder="${esc(e?.bankName ?? 'Card')}${e?.last4 ? ` ...${esc(e.last4)}` : ''}"></label>`;
  return screen(COMPARE_TITLE, `${checks}${name}<div class="btns"><button class="btn primary" data-a="flow-add">Continue</button></div>`, 'compare');
}

/**
 * Gmail's forwarding confirmation: only its confirm link (".../mail/vf-..."), never the cancel link
 * that comes in the same email (confirmLink() in shared/card-flow.ts). There is no code to type.
 */
function verify(f: FlowState, d: OverviewDto): string {
  const g = d.setup.gmailConfirmation;
  const steps = gmailForwardSteps(d.user.forwardAddress);
  const link = g ? confirmLink(g.links) : null;
  const confirm = g ? (link ? `<p class="hint">${esc(VERIFY_LINK)}</p>${copyRow('flink', link, 'Link')}` : `<p class="hint">${esc(VERIFY_NO_LINK)}</p>`) : '';
  const which = f.gmail ? `<p class="hint">Do this in ${esc(f.gmail)}.</p>` : '';
  return screen(
    VERIFY_TITLE,
    `${which}<ol>${steps.map((s) => `<li>${esc(s)}</li>`).join('')}</ol>
  ${checkRow(g ? VERIFY_ARRIVED : VERIFY_WAIT, !!g)}${confirm}
  <p class="hint">${esc(FILTER_LEAVE_FORWARDING)}</p>
  <div class="btns"><button class="btn primary" data-a="flow-verified"${g ? '' : ' disabled'}>${esc(VERIFY_DONE)}</button></div>`,
    'verify',
  );
}

/**
 * The Gmail filter: From, plus the one box that picks out purchase alerts (shared/filter-choice.ts):
 * Subject when it never changes, else "Has the words" with a phrase from the alert, else From only
 * with a plain warning. Each box has its own Copy button, and a line says why.
 */
function filter(f: FlowState, d: OverviewDto): string {
  const c = f.compare;
  if (!c || c.result.kind !== 'new') return '';
  const r = c.result;
  const bank = f.example?.bankName ?? 'Your bank';
  const box = r.words
    ? `<div class="field"><span>${esc(FILTER_WORDS_LABEL)}</span>${copyRow('flt-words', quotedPhrase(r.words), FILTER_WORDS_LABEL)}</div><p class="hint">${esc(FILTER_WORDS_HOW)}</p>`
    : r.subject
      ? `<div class="field"><span>Subject</span>${copyRow('flt-subject', r.subject, 'Subject')}</div>`
      : '';
  const why = r.words
    ? [FILTER_WHY_WORDS(bank), SUBJECT_WHY[r.subjectWhy ?? 'general'](bank)]
    : r.subject
      ? [FILTER_WHY_SUBJECT(bank), r.cardSpecific ? CARD_SPECIFIC : '']
      : [];
  const notes = why.filter(Boolean).map((n) => `<p class="hint">${esc(n)}</p>`).join('');
  const warning = r.words || r.subject ? '' : `<p class="never">${esc(FROM_ONLY_WARNING(r.from))}</p>`;
  return screen(
    FILTER_TITLE,
    `<p><b>${esc(FILTER_COMPUTER)}</b></p>
  <ol><li>${esc(FILTER_OPEN(f.gmail))}</li><li>${esc(FILTER_FIELDS_ONLY)}</li></ol>
  <div class="field"><span>From</span>${copyRow('flt-from', r.from, 'From')}</div>
  ${box}${notes}${warning}
  <ol start="3"><li>${esc(FILTER_FORWARD)}</li><li>${esc(FILTER_LEAVE_FORWARDING)}</li></ol>
  ${copyRow('flt-hush', d.user.forwardAddress, 'Address')}
  <div class="btns"><button class="btn primary" data-a="flow-next" data-to="done">Filter is made</button></div>`,
    'filter',
  );
}

/** A bank Hush already reads: only this card's alerts are left to turn on (the same steps as above). */
function cardAlerts(f: FlowState): string {
  const bank = f.example ? bankBySender(f.example.sender) : undefined;
  const covered = f.compare?.result.kind === 'covered' ? `<p><b>${esc(COVERED)}</b></p>` : '';
  return screen(CARD_ALERTS_TITLE, `${covered}<p>${esc(CARD_ALERTS_BODY)}</p>${alertsBody(bank, 'data-a="flow-next" data-to="done"')}`, 'card-alerts');
}

/**
 * The end: nothing to wait for. The card shows on Cards and banks as waiting for its first purchase
 * until one comes through the filter (shared/card-status.ts), as before.
 */
function done(): string {
  return screen(DONE_TITLE, `<p>${esc(DONE_BODY)}</p><div class="btns"><button class="btn primary" data-a="flow-close">Done</button></div>`, 'done');
}

/** The whole flow, for its current step. */
export function vCardFlow(d: OverviewDto, f: FlowState): string {
  const bodies: Record<FlowStep, () => string> = {
    gate,
    'alerts-on': alertsOn,
    'not-sure': notSure,
    gmail: () => gmail(f, d),
    example: () => example(f, d),
    compare: () => compare(f),
    verify: () => verify(f, d),
    filter: () => filter(f, d),
    'card-alerts': () => cardAlerts(f),
    done,
  };
  return `<div class="flow">${header(f, d)}${bodies[f.step]()}</div>`;
}
